// frontend/src/components/hotel/GuestContactTab.jsx
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

const EMPTY = { label: { fr: "", en: "" }, target: "", icon: "", order: 0, enabled: true, hours: null, key_ref: "" };

export default function GuestContactTab({ hotelId }) {
  const [items, setItems] = useState([]);
  const [keys, setKeys] = useState([]);   // touches type 9 (source des n° 3CX)
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get(`/hotels/${hotelId}/guest-destinations`).then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => {
    load();
    api.get(`/hotels/${hotelId}/speed-dials`).then((r) => setKeys(Array.isArray(r.data) ? r.data : [])).catch(() => setKeys([]));
  }, [hotelId]);

  // Lier la destination à une touche type 9 : la cible suit la touche (source 3CX).
  const pickKey = (k) => {
    if (!k) return setForm((f) => ({ ...f, key_ref: "" }));
    const sd = keys.find((x) => x.key === k);
    setForm((f) => ({ ...f, key_ref: k, target: (sd && sd.target) || f.target }));
  };

  const add = async () => {
    try {
      await api.post(`/hotels/${hotelId}/guest-destinations`, form);
      toast.success("Destination ajoutée"); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (d) => {
    try { await api.put(`/hotels/${hotelId}/guest-destinations/${d.key}`, { ...d, enabled: !d.enabled }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (d) => {
    try { await api.delete(`/hotels/${hotelId}/guest-destinations/${d.key}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-sm border border-zinc-200 bg-white p-4">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Nouvelle destination</h3>
        {keys.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <select data-testid="gc-key-ref" value={form.key_ref} onChange={(e) => pickKey(e.target.value)}
              className="rounded-sm border border-zinc-200 px-2 py-1.5 text-xs">
              <option value="">Cible manuelle</option>
              {keys.map((k) => (
                <option key={k.key} value={k.key}>Touche {k.key} · {k.label} → {k.target}</option>
              ))}
            </select>
            {form.key_ref && <span className="text-[11px] text-blue-700">La cible suit la touche {form.key_ref} (extension 3CX).</span>}
          </div>
        )}
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-5">
          <input data-testid="gc-label-fr" placeholder="Libellé FR" value={form.label.fr}
            onChange={(e) => setForm({ ...form, label: { ...form.label, fr: e.target.value } })}
            className="rounded-sm border border-zinc-200 px-2 py-1.5 text-xs" />
          <input placeholder="Libellé EN" value={form.label.en}
            onChange={(e) => setForm({ ...form, label: { ...form.label, en: e.target.value } })}
            className="rounded-sm border border-zinc-200 px-2 py-1.5 text-xs" />
          <input data-testid="gc-target" placeholder="N° cible (3CX)" value={form.target} disabled={!!form.key_ref}
            onChange={(e) => setForm({ ...form, target: e.target.value })}
            className="rounded-sm border border-zinc-200 px-2 py-1.5 font-mono text-xs disabled:bg-zinc-100 disabled:text-zinc-500" />
          <input placeholder="Icône (bell…)" value={form.icon}
            onChange={(e) => setForm({ ...form, icon: e.target.value })}
            className="rounded-sm border border-zinc-200 px-2 py-1.5 text-xs" />
          <Button data-testid="gc-add" onClick={add} disabled={!form.label.fr || !form.target}
            className="rounded-sm bg-blue-600 hover:bg-blue-700"><Plus className="mr-1 h-4 w-4" />Ajouter</Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-sm border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-100 text-left text-[9px] uppercase tracking-[0.15em] text-zinc-400">
            <th className="px-4 py-2 font-bold">Libellé</th><th className="px-4 py-2 font-bold">Cible</th>
            <th className="px-4 py-2 font-bold">Statut</th><th className="px-4 py-2"></th>
          </tr></thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.key} data-testid={`gc-row-${d.key}`} className="border-b border-zinc-50 last:border-0">
                <td className="px-4 py-2">{d.label?.fr} <span className="text-zinc-400">/ {d.label?.en}</span></td>
                <td className="px-4 py-2 font-mono text-xs text-zinc-600">
                  {d.target}
                  {d.key_ref && (
                    <span className="ml-2 rounded-sm border border-blue-200 bg-blue-50 px-1 py-0.5 font-sans text-[10px] font-semibold text-blue-700">touche {d.key_ref}</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <button onClick={() => toggle(d)} className={`rounded-sm px-2 py-0.5 text-[10px] font-bold ${d.enabled ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-400"}`}>
                    {d.enabled ? "Actif" : "Inactif"}
                  </button>
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => del(d)} className="text-zinc-300 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-zinc-400">Aucune destination. Ajoutez la réception, le restaurant…</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
